import { Link } from '@tanstack/react-router';
import { ChevronDownIcon, PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTaskDraftStore } from '@/features/tasks';
import type { WorkflowView } from '@/lib/api/generated/model';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * New task, from the page that lists every task (spec 127 FR-013).
 *
 * A task belongs to a workflow, so the first choice is which one. Choosing
 * opens that workflow's task draft — the same draft a board's + opens, which
 * the dock shows on any page (spec 121) — so the form, its draft and where the
 * task lands are the board's own.
 */
function NewTaskMenuControl({
  workspaceId,
  workflows,
}: {
  workspaceId: string;
  workflows: readonly WorkflowView[];
}) {
  const openDraft = useTaskDraftStore((state) => state.openDraft);
  const published = workflows.filter((w) => w.status === 'published');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button size="sm" />}>
        <PlusIcon aria-hidden="true" strokeWidth={1.5} />
        New task
        <ChevronDownIcon aria-hidden="true" strokeWidth={1.5} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {published.length === 0 ? (
          <div className="flex flex-col gap-1.5 px-2 py-1.5 text-sm">
            <p className="text-muted-foreground">No published workflow yet.</p>
            <Link
              to="/workspaces/$workspaceId/workflows"
              params={{ workspaceId }}
              className="text-foreground underline underline-offset-4"
            >
              Open Workflows
            </Link>
          </div>
        ) : (
          <DropdownMenuGroup>
            <DropdownMenuLabel>On the board of</DropdownMenuLabel>
            {published.map((workflow) => (
              <DropdownMenuItem
                key={workflow.id}
                onClick={() => {
                  openDraft(workspaceId, workflow.id);
                }}
              >
                {workflow.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Creating a task is for the workspace's members (spec 189). */
export function NewTaskMenu(props: Parameters<typeof NewTaskMenuControl>[0]) {
  return useCanWork() ? <NewTaskMenuControl {...props} /> : null;
}
